import { useState, useEffect, useRef, useCallback } from 'react';

const features = [
  {
    icon: '/landing/EB_SYMB_Wohnung-leer.webp',
    title: 'Leerräumen und verbessern',
    description: 'Möblierte Räume in Sekunden digital leer räumen und die Bildqualität massiv verbessern. Perfekt für Immobilien, die noch bewohnt sind oder sich sogar noch im Bau befinden.',
    pairs: [
      ['/landing/ES_IMG_Web_Kueche_vorher.webp', '/landing/ES_IMG_Web_Kueche_leer.webp'],
      ['/landing/ES_IMG_Web_Schlafzimmer_vorher.webp', '/landing/ES_IMG_Web_Schlafzimmer_leer.webp'],
      ['/landing/ES_IMG_Web_Wohnzimmer1_vorher.webp', '/landing/ES_IMG_Web_Wohnzimmer1_leer.webp'],
    ],
    type: 'slideshow' as const,
  },
  {
    icon: '/landing/EB_SYMB_Wohnung-staged.webp',
    title: 'Virtuelles Staging',
    description: 'Leere Räume virtuell in verschiedenen Stilen einrichten — Modern, Industriell, Skandinavisch und mehr.',
    pairs: [
      ['/landing/ES_IMG_Web_Kueche_leer.webp', '/landing/ES_IMG_Web_Kueche1_gruen.webp'],
      ['/landing/ES_IMG_Web_Schlafzimmer2_leer.webp', '/landing/ES_IMG_Web_Schlafzimmer2_luxus.webp'],
      ['/landing/ES_IMG_Web_Wohnzimmer1_vorher.webp', '/landing/ES_IMG_Web_Wohnzimmer1_industrial.webp'],
    ],
    type: 'slideshow' as const,
  },
  {
    icon: '/landing/EB_SYMB_Wohnung-Grundriss.png',
    title: '3D Grundrisse erstellen',
    description: 'Verwandle flache 2D-Zeichnungen in fotorealistische, möblierte 3D-Grundrisse. Schneide einzelne Wohneinheiten aus Flächengrundrissen aus und stelle deine Immobilie hochwertig dar.',
    pairs: [
      ['/landing/grundriss/ES_IMG_Grundriss1_vorher.jpg', '/landing/grundriss/ES_IMG_Grundriss1_3D.jpg'],
      ['/landing/grundriss/ES_IMG_Grundriss2_vorher.jpg', '/landing/grundriss/ES_IMG_Grundriss2_3D.jpg'],
      ['/landing/grundriss/ES_IMG_Grundriss3_vorher.jpg', '/landing/grundriss/ES_IMG_Grundriss3_3D.jpg'],
    ],
    type: 'slideshow' as const,
  },
  {
    icon: '/landing/EB_SYMB_upload.webp',
    title: 'Mieter-Upload',
    description: 'Mieter können eigene Fotos direkt hochladen — für einen nahtlosen Workflow ohne E-Mail-Chaos.',
    image: '/landing/ES_IMG_Web_Foto-Kueche.webp',
    type: 'static' as const,
  },
  {
    icon: '/landing/EB_SYMB_upscale.webp',
    title: 'KI-Upscaling',
    description: 'Bilder automatisch in höchster Auflösung ausgeben — perfekt für Print und große Formate.',
    sliderBefore: '/landing/ES_IMG_Web_Upscale_vorher.webp',
    sliderAfter: '/landing/ES_IMG_Web_Upscale_nachher.webp',
    type: 'slider' as const,
  },
  {
    icon: '/landing/EB_SYMB_download.webp',
    title: 'Branding & Export',
    description: 'Perfekt abgestimmtes Branding: Erstelle dein eigenes Overlay mit Logo, Weichzeichnen oder Rahmen und wende es automatisch auf alle Bilder an. Exportiere die Bilder dann bequem für alle Immobilien-Portale oder erstelle direkt ein Exposé.',
    brandingImages: [
      '/landing/ES_IMG_Web_Overlay_orange.webp',
      '/landing/ES_IMG_Web_Overlay_dunkel.webp',
      '/landing/ES_IMG_Web_Overlay_blau.webp',
      '/landing/ES_IMG_Web_Overlay_gold.webp',
      '/landing/ES_IMG_Web_Overlay_hell.webp',
    ],
    type: 'branding' as const,
  },
];

const BRANDING_DURATION = 3500;

const HOLD = 2000;
const FADE = 600;
const PAIR_DURATION_MS = HOLD + FADE; // duration the progress bar fills for one Vorher/Nachher state

/* ── Before / After Slider ─────────────────────────────────────── */

const BeforeAfterSlider: React.FC<{ before: string; after: string }> = ({ before, after }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const updatePos = useCallback((clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => { if (dragging.current) updatePos(e.clientX); };
    const onTouchMove = (e: TouchEvent) => { if (dragging.current) updatePos(e.touches[0].clientX); };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [updatePos]);

  return (
    <div
      ref={containerRef}
      className="lp-slider-container"
      onMouseDown={(e) => { dragging.current = true; updatePos(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; updatePos(e.touches[0].clientX); }}
    >
      {/* After image (full) */}
      <img src={after} alt="Nachher" className="lp-slider-img" draggable={false} />
      {/* Before image (clipped via clip-path) */}
      <img
        src={before}
        alt="Vorher"
        className="lp-slider-img lp-slider-img-before"
        draggable={false}
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />
      {/* Handle */}
      <div className="lp-slider-handle" style={{ left: `${pos}%` }}>
        <div className="lp-slider-line" />
        <div className="lp-slider-knob">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 4l-6 8 6 8" /><path d="M16 4l6 8-6 8" />
          </svg>
        </div>
      </div>
      {/* Labels */}
      <div className="lp-slider-label lp-slider-label-before">Vorher</div>
      <div className="lp-slider-label lp-slider-label-after">Nachher</div>
    </div>
  );
};

/* ── Features Section ──────────────────────────────────────────── */

export const FeaturesSection: React.FC = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [displayedImg, setDisplayedImg] = useState('/landing/ES_IMG_Web_Kueche_vorher.webp');
  const [fadingImg, setFadingImg] = useState<string | null>(null);
  const [fadeIn, setFadeIn] = useState(false);
  const [pairIndex, setPairIndex] = useState(0);
  const [labelActive, setLabelActive] = useState<'before' | 'after'>('before');
  const [labelKey, setLabelKey] = useState(0); // bumped on every state change → restarts CSS animation
  const [brandingIdx, setBrandingIdx] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
  };

  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
  };

  const crossfadeTo = useCallback((src: string, cb?: () => void) => {
    setFadingImg(src);
    setFadeIn(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setFadeIn(true);
        later(() => {
          setDisplayedImg(src);
          setFadingImg(null);
          setFadeIn(false);
          if (cb) cb();
        }, FADE);
      });
    });
  }, []);

  /**
   * Run the slideshow for `featureIdx`.
   * Starts at `startPairIdx` / `startState` and loops forever inside this feature
   * (no auto-advance to the next feature — that was confusing).
   */
  const startSlideshow = useCallback(
    (featureIdx: number, startPairIdx = 0, startState: 'before' | 'after' = 'before') => {
      clearTimers();
      const feature = features[featureIdx];
      if (!feature || feature.type !== 'slideshow') return;
      const pairs = feature.pairs;

      const showState = (pairIdx: number, state: 'before' | 'after') => {
        const pair = pairs[pairIdx % pairs.length];
        const src = state === 'before' ? pair[0] : pair[1];
        setPairIndex(pairIdx % pairs.length);
        setLabelActive(state);
        setLabelKey(k => k + 1);

        crossfadeTo(src, () => {
          later(() => {
            if (state === 'before') {
              showState(pairIdx, 'after');
            } else {
              showState(pairIdx + 1, 'before');
            }
          }, HOLD);
        });
      };

      showState(startPairIdx, startState);
    },
    [crossfadeTo],
  );

  const activateFeature = useCallback((idx: number) => {
    clearTimers();
    if (autoAdvanceRef.current) { clearTimeout(autoAdvanceRef.current); autoAdvanceRef.current = null; }
    setActiveFeature(idx);

    const feature = features[idx];
    if (feature.type === 'static') {
      setDisplayedImg(feature.image!);
      setFadingImg(null);
      setLabelActive('before');
    } else if (feature.type === 'slider') {
      setFadingImg(null);
    } else if (feature.type === 'branding') {
      setBrandingIdx(0);
      setDisplayedImg(feature.brandingImages![0]);
      setFadingImg(null);
    } else {
      startSlideshow(idx);
    }
  }, [startSlideshow]);

  useEffect(() => {
    activateFeature(0);
    return () => clearTimers();
  }, []);

  // Branding slider auto-advance (fades between images, loops forever inside the
  // branding feature). User can click thumbs to jump to a specific image.
  useEffect(() => {
    const feature = features[activeFeature];
    if (feature.type !== 'branding' || !feature.brandingImages) return;
    const imgs = feature.brandingImages;
    autoAdvanceRef.current = setTimeout(() => {
      const next = (brandingIdx + 1) % imgs.length;
      crossfadeTo(imgs[next], () => setBrandingIdx(next));
    }, BRANDING_DURATION);
    return () => { if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current); };
  }, [brandingIdx, activeFeature, crossfadeTo]);

  /**
   * Manual jump to a specific branding image — clear auto-advance, crossfade,
   * then the useEffect above will pick up auto-advance from the new index.
   */
  const goToBrandingImage = useCallback((nextIdx: number) => {
    if (autoAdvanceRef.current) { clearTimeout(autoAdvanceRef.current); autoAdvanceRef.current = null; }
    const feature = features[activeFeature];
    if (feature.type !== 'branding' || !feature.brandingImages) return;
    if (nextIdx === brandingIdx) return;
    crossfadeTo(feature.brandingImages[nextIdx], () => setBrandingIdx(nextIdx));
  }, [activeFeature, brandingIdx, crossfadeTo]);

  /**
   * Click handler for the Vorher/Nachher tabs — jump to that state in the
   * current pair and continue the slideshow from there.
   */
  const handleLabelClick = useCallback((state: 'before' | 'after') => {
    if (state === labelActive) return;
    startSlideshow(activeFeature, pairIndex, state);
  }, [activeFeature, pairIndex, labelActive, startSlideshow]);

  const feature = features[activeFeature];
  const isSlideshow = feature.type === 'slideshow';
  const isSlider = feature.type === 'slider';
  const isBranding = feature.type === 'branding';

  return (
    <>
      {/* Value Section 1 */}
      <section className="lp-value-section-1">
        <div className="lp-value-section-1-inner">
          <div className="lp-section-heading-center">
            <h2>Alles, was Immobilien-Profis<br />brauchen</h2>
            <p>Selbst Amateuraufnahmen werden im Handumdrehen<br />zu wohnlichen Profi-Aufnahmen.</p>
          </div>
          <div className="lp-value-images-row">
            <div className="lp-value-image-card">
              <img src="/landing/ES_IMG_Web_Balkon1_vorher.webp" alt="Original" />
              <div className="lp-value-pill lp-value-pill--original">Original</div>
            </div>
            <div className="lp-value-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </div>
            <div className="lp-value-image-card">
              <img src="/landing/ES_IMG_Web_Balkon1_leer.webp" alt="Leer" />
              <div className="lp-value-pill lp-value-pill--leer">Leer</div>
            </div>
            <div className="lp-value-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </div>
            <div className="lp-value-image-card lp-value-image-card--staged">
              <img src="/landing/ES_IMG_Web_Balkon1_nachher.webp" alt="Staged" />
              <div className="lp-value-pill lp-value-pill--staged">Staged</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Section 2 — Features Accordion */}
      <section className="lp-value-section-2" id="features">
        <div className="lp-value-section-2-inner">
          <div className="lp-features-badge">
            Die intelligenteste Lösung für Makler
          </div>
          <h2>Funktionen, die deine Arbeit<br />erleichtern!</h2>
          <div className="lp-features-layout">
            <div className="lp-features-list">
              {features.map((f, i) => {
                const isActive = activeFeature === i;
                return (
                  <div
                    key={i}
                    className={`lp-feature-item${isActive ? ' active' : ''}`}
                    onClick={() => activateFeature(i)}
                  >
                    <div className="lp-feature-item-header">
                      <div className="lp-feature-item-icon">
                        <img src={f.icon} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} />
                      </div>
                      <span className="lp-feature-item-title">{f.title}</span>
                    </div>
                    <div className="lp-feature-item-body">
                      <p>{f.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={`lp-features-image${isBranding ? ' is-branding' : ''}`}>
              {isSlider && feature.sliderBefore && feature.sliderAfter ? (
                <BeforeAfterSlider before={feature.sliderBefore} after={feature.sliderAfter} />
              ) : isBranding && feature.brandingImages ? (
                <>
                  <h3 className="lp-branding-heading">Mit eigenem Overlay!</h3>
                  <div className="lp-branding-card">
                    <img
                      src={displayedImg}
                      alt="Branding Beispiel"
                      style={{ opacity: 1 }}
                    />
                    {fadingImg && (
                      <img
                        src={fadingImg}
                        alt=""
                        aria-hidden="true"
                        style={{
                          opacity: fadeIn ? 1 : 0,
                          transition: `opacity ${FADE}ms ease`,
                        }}
                      />
                    )}
                  </div>
                  {/* Clickable thumbs — same UX as slideshow features */}
                  <div className="lp-branding-thumb-bar">
                    {feature.brandingImages.map((src, thumbIdx) => (
                      <button
                        key={thumbIdx}
                        type="button"
                        className={`lp-feature-thumb${thumbIdx === brandingIdx ? ' active' : ''}`}
                        onClick={() => goToBrandingImage(thumbIdx)}
                        aria-label={`Branding-Variante ${thumbIdx + 1} anzeigen`}
                      >
                        <img src={src} alt="" />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {/* Base image */}
                  <img src={displayedImg} alt="Feature Vorschau" style={{ opacity: 1 }} />
                  {/* Fading-in image */}
                  {fadingImg && (
                    <img
                      src={fadingImg}
                      alt="Feature Vorschau"
                      style={{
                        opacity: fadeIn ? 1 : 0,
                        transition: `opacity ${FADE}ms ease`,
                      }}
                    />
                  )}
                  {/* Vorher/Nachher tabs with progress fill — clickable */}
                  {isSlideshow && (
                    <div className="lp-feature-label">
                      {(['before', 'after'] as const).map(state => {
                        const isActiveTab = state === labelActive;
                        return (
                          <button
                            type="button"
                            key={`${state}-${labelKey}-${isActiveTab}`}
                            className={`lp-feature-label-tag${isActiveTab ? ' active' : ''}`}
                            onClick={() => handleLabelClick(state)}
                          >
                            <span className="lp-progress-fill" />
                            <span>{state === 'before' ? 'Vorher' : 'Nachher'}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {/* Thumbs for slideshow features — clickable to jump to that pair */}
                  {isSlideshow && feature.pairs && (
                    <div className="lp-feature-thumb-bar">
                      {feature.pairs.map((pair, thumbIdx) => (
                        <button
                          key={thumbIdx}
                          type="button"
                          className={`lp-feature-thumb${thumbIdx === pairIndex ? ' active' : ''}`}
                          onClick={() => startSlideshow(activeFeature, thumbIdx)}
                          aria-label={`Beispiel ${thumbIdx + 1} anzeigen`}
                        >
                          <img src={pair[0]} alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// PAIR_DURATION_MS is exported indirectly via CSS; keep it at module scope for
// the CSS-side animation (`lp-feature-progress` keyframes set to 2.6s).
void PAIR_DURATION_MS;
